import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { Store, UpdateFn } from '../../../services/store';
import { toVoid } from '../../../utils/void';
import { addDays, diffDays, todayIso } from '../../../utils/date-only';
import { ProductionPlannerProvider } from './production-planner.provider';
import { Order, OrderStatus } from '../orders-new/models/order.dto';
import { DEFAULT_PLAN_DAYS, PlannerDayVm, PlannerDrag, PlannerMonthVm, PlannerRowVm, PlannerViewModel, TrayChipVm } from './models/planner.view-model';

export interface ProductionPlannerState {
  loading: boolean;
  orders: Order[];
  drag: PlannerDrag | null;
  rangeStart: string;
  dayCount: number;
  todayOffset: number;
  days: PlannerDayVm[];
  months: PlannerMonthVm[];
  rows: PlannerRowVm[];
  tray: TrayChipVm[];
}

const INITIAL_STATE: ProductionPlannerState = {
  loading: false,
  orders: [],
  drag: null,
  rangeStart: todayIso(),
  dayCount: 0,
  todayOffset: 0,
  days: [],
  months: [],
  rows: [],
  tray: []
};

@Injectable({ providedIn: 'root' })
export class ProductionPlannerStore extends Store<ProductionPlannerState> {
  constructor(private provider: ProductionPlannerProvider) {
    super(INITIAL_STATE);
  }

  init(): Observable<void> {
    this.update(state => ({ ...state, loading: true }));

    return this.provider.getOrders()
      .pipe(
        tap(orders => {
          this.update(
            state => ({ ...state, loading: false, orders }),
            deriveRange,
            deriveViews
          );
        }),
        toVoid()
      );
  }

  dragStart(drag: PlannerDrag): void {
    this.update(state => ({ ...state, drag }), deriveViews);
  }

  dragMovedBy(dayDelta: number): void {
    const drag = this.state.drag;

    if (!drag || drag.kind === 'fromTray' || drag.dayDelta === dayDelta) {
      return;
    }

    this.update(state => ({ ...state, drag: { ...drag, dayDelta } }), deriveViews);
  }

  trayDragMoved(overOffset: number | null): void {
    const drag = this.state.drag;

    if (!drag || drag.kind !== 'fromTray' || drag.overOffset === overOffset) {
      return;
    }

    this.update(state => ({ ...state, drag: { ...drag, overOffset } }), deriveViews);
  }

  dragCancelled(): void {
    if (!this.state.drag) {
      return;
    }

    this.update(state => ({ ...state, drag: null }), deriveViews);
  }

  dragCommitted(): void {
    const drag = this.state.drag;

    if (!drag) {
      return;
    }

    if (drag.kind === 'fromTray') {
      if (drag.overOffset === null) {
        this.dragCancelled();
        return;
      }

      const start = addDays(this.state.rangeStart, drag.overOffset);
      this.applyPlan(drag.orderId, start, addDays(start, DEFAULT_PLAN_DAYS - 1));
      return;
    }

    const order = this.state.orders.find(x => x.id === drag.orderId);
    const patch = order ? resolveDrag(drag, order) : null;

    if (!patch) {
      this.dragCancelled();
      return;
    }

    this.applyPlan(drag.orderId, patch.start, patch.end);
  }

  unplanOrder(orderId: number): void {
    this.applyPlan(orderId, null, null);
  }

  private applyPlan(orderId: number, start: string | null, end: string | null): void {
    const previousOrders = this.state.orders;

    this.update(
      state => ({
        ...state,
        drag: null,
        orders: state.orders.map(order => order.id === orderId
          ? {
              ...order,
              plannedStartDate: start,
              plannedEndDate: end,
              status: nextStatus(order.status, start !== null)
            }
          : order)
      }),
      deriveRange,
      deriveViews
    );

    this.provider.planOrder(orderId, start, end).subscribe({
      error: error => {
        console.error('Nie udało się zapisać planu zlecenia', error);
        this.update(state => ({ ...state, orders: previousOrders }), deriveRange, deriveViews);
      }
    });
  }
}

function nextStatus(status: OrderStatus, planned: boolean): OrderStatus {
  if (planned && status === OrderStatus.new) {
    return OrderStatus.planned;
  }

  if (!planned && status === OrderStatus.planned) {
    return OrderStatus.new;
  }

  return status;
}

function resolveDrag(drag: Exclude<PlannerDrag, { kind: 'fromTray' }>, order: Order): { start: string; end: string } | null {
  if (drag.dayDelta === 0) {
    return null;
  }

  const start = order.plannedStartDate!;
  const end = order.plannedEndDate!;

  if (drag.kind === 'move') {
    return { start: addDays(start, drag.dayDelta), end: addDays(end, drag.dayDelta) };
  }

  if (drag.kind === 'resizeStart') {
    const moved = addDays(start, drag.dayDelta);
    return { start: diffDays(moved, end) < 0 ? end : moved, end };
  }

  const moved = addDays(end, drag.dayDelta);
  return { start, end: diffDays(start, moved) < 0 ? start : moved };
}

const deriveRange: UpdateFn<ProductionPlannerState> = state => {
  const { rangeStart, dayCount } = PlannerViewModel.computeRange(state.orders);

  return {
    ...state,
    rangeStart,
    dayCount,
    todayOffset: diffDays(rangeStart, todayIso()),
    days: PlannerViewModel.mapDays(rangeStart, dayCount),
    months: PlannerViewModel.mapMonths(rangeStart, dayCount)
  };
};

const deriveViews: UpdateFn<ProductionPlannerState> = state => ({
  ...state,
  rows: PlannerViewModel.mapRows(state.orders, state.rangeStart, state.drag),
  tray: PlannerViewModel.mapTray(state.orders)
});
